import { useMemo, type ReactElement } from 'react';
import Flex from '@creditflowresearch/uilib/Flex';
import { Select } from '@creditflowresearch/uilib/Select';
import { Label } from '@creditflowresearch/uilib/Badge/Label';
import {
  useBondScreenerViewsContext,
} from '@/creditflowresearch/application/BondScreener/Sidebar/BondScreenerViewsContext';
import LoginOrSignUpPopover from '@/creditflowresearch/ui/common/components/LoginOrSignUpPopover';
import { DEFAULT_VIEW_OPTION, buildViewOptions, type IViewOption } from './bondScreenerViewOptions';

import styles from './customizationPanel.module.scss';

const formatOptionLabel = ({ label }: IViewOption, { context }: { context: string }): ReactElement => (
  <Flex align="center" gap={ 6 }>
    { context === 'value' && <Label backgroundColor="black" className={ styles.viewBadge }>VIEW</Label> }
    <span>{ label }</span>
  </Flex>
);

interface ICustomizationPanelViewSelectProps {
  isRestricted?: boolean,
}

export const CustomizationPanelViewSelect = ({ isRestricted = false }: ICustomizationPanelViewSelectProps) => {
  const { views, activeViewId, selectView } = useBondScreenerViewsContext();

  const options = useMemo<IViewOption[]>(() => buildViewOptions(views), [views]);

  const selectedOption = options.find((option) => option.value === activeViewId) ?? DEFAULT_VIEW_OPTION;

  const handleChange = (option: IViewOption | null): void => {
    selectView(option && option.value !== DEFAULT_VIEW_OPTION.value ? option.value : null);
  };

  return (
    <Flex direction="column" gap={ 10 } className={ styles.viewSelectRow } dataTest="customizationPanelViewSelect">
      <LoginOrSignUpPopover isRestricted={ isRestricted } placement="bottom-start">
        <Select
          value={ selectedOption }
          options={ options }
          onChange={ handleChange }
          formatOptionLabel={ formatOptionLabel }
          isSearchable={ false }
          dataTest="customizationPanelViewSelect"
          className={ styles.viewSelect }
          formFieldClassName={ styles.viewSelectField }
          isDisabled={ isRestricted }
        />
      </LoginOrSignUpPopover>
      <p className={ styles.description }>
        Build your ideal workspace. Set your filters, select and arrange
        data columns, and save it as a custom view. Create multiple views
        to streamline your workflows and switch between them with one click.
      </p>
    </Flex>
  );
};
